import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { Bell, MailOpen, CheckCheck, ChevronRight, X } from 'lucide-react'
import { request, isSessionEndedError } from '@papan/api-client'
import { describeNotification, notificationActor, resolveNotificationOutcome, type NotificationData } from '@papan/types'
import { avatarBg, getInitials } from './avatar'

export interface NotificationItem {
  id: string
  /** The row's OWN `tenant_id`, which is not necessarily the tenant the
   *  user is acting in — the bell lists by user and the table is not
   *  tenant-filtered.
   *
   *  NOT "the tenant to act against", and do not read it that way. It is
   *  whichever tenant WROTE the row, which for a cross-org type is the
   *  other side: a `share_request_received` carries the INITIATOR's tenant
   *  to a recipient who is not a member of it. Such a type takes its
   *  actionable tenant from its own `data` — for an org-targeted reshare
   *  that is `data.targetTenantId`. (The row's `relatedTenantId` COLUMN is
   *  a different thing and this endpoint does not serve it; an earlier
   *  note named it here by mistake, and the correction then over-shot into
   *  "there is no such field at all". Round 12, finding 6.)
   *
   *  The chat-storage types are written by the tenant their action belongs
   *  to, so for those the two coincide and the handlers use it.
   *
   *  (The column's schema comment says it exists for deletion cleanup and
   *  is not used for list filtering. Still true — this exposes it to a
   *  handler, not to a query.)
   *
   *  Optional for deploy skew with an API that predates it. */
  tenantId?: string
  type: string
  title: string
  body?: string
  data?: Record<string, unknown>
  read: boolean
  createdAt: string
  /** Server-resolved: whether the underlying request is still actionable.
   *  Absent on freshly-pushed live notifications (treated as actionable);
   *  set to false once the request is cancelled/expired/established etc. */
  actionable?: boolean
  /** Server-resolved: the underlying request's CURRENT state. Lets a settled row
   *  say WHICH way it settled (approved / declined / expired) — `actionable`
   *  flattens all of those to one `false`. Absent on live-pushed rows. */
  requestState?: string
  /** Set by the bell when the row's SECOND action was clicked, so one
   *  handler can tell the two replies apart without a second callback
   *  threaded through every caller. */
  secondary?: boolean
}

/** Outcome badge on a settled row. No shared pill primitive exists in core-ui
 *  (every other badge here is a red numeric count), so keep it local and sized
 *  to the row's own 10px scale. */
const BADGE_TONE: Record<'success' | 'danger' | 'muted', string> = {
  success: 'bg-emerald-500/15 text-emerald-400',
  danger: 'bg-red-500/15 text-red-400',
  muted: 'themed-bg-tertiary themed-text-muted',
}

export interface NotificationBellProps {
  /** Subscribe to live notifications — returns unsubscribe function.
   *  Injected by the parent (e.g., from useRealtime().onNotification). */
  onSubscribe?: (handler: (n: { id: string; type: string; title: string; body?: string; data?: Record<string, unknown>; createdAt: string }) => void) => () => void
  /** Called when an actionable notification row is clicked. The parent
   *  (app layer) opens the appropriate feature popup / navigates. Whether a
   *  row IS actionable is decided by the registry (describeNotification.action)
   *  + the server's per-row `actionable` stamp — no hand-passed type list. */
  onAction?: (n: NotificationItem) => void
  /** Where the panel opens relative to the bell. 'rail' (default) opens to
   *  the right of the desktop taskbar rail; 'top-right' drops down-left from
   *  a top bar; 'mobile' is a full-width sheet under the header (phones).
   *  Pure positioning — behavior identical. */
  placement?: 'rail' | 'top-right' | 'mobile'
}

export function NotificationBell({ onSubscribe, onAction, placement = 'rail' }: NotificationBellProps) {
  const [open, setOpen] = useState(false)
  const [notifications, setNotifications] = useState<NotificationItem[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)

  // Merge the server's list with any optimistic/live entries already in
  // state by id — the server is authoritative for everything it returned,
  // but in-flight WS prepends that arrived between fetch dispatch and
  // response shouldn't get clobbered, and `read` flags toggled locally
  // (before a server confirm round-trip) should survive too. The
  // dedup keys on `id` so the realtime+persisted pair (which share an id
  // on purpose — see notifyUserByEmail) collapse to one row.
  const mergeNotifications = (server: NotificationItem[], local: NotificationItem[]): NotificationItem[] => {
    const localById = new Map(local.map((n) => [n.id, n]))
    const seen = new Set<string>()
    const out: NotificationItem[] = []
    for (const n of server) {
      // F23: honor a locally-toggled `read` (optimistic mark-read) — if the user
      // read it locally but the server row is still unread (e.g. the PATCH /read
      // was swallowed), keep it read instead of flipping back to unread.
      const localCopy = localById.get(n.id)
      out.push(localCopy?.read && !n.read ? { ...n, read: true } : n)
      seen.add(n.id)
    }
    for (const n of local) {
      if (!seen.has(n.id)) {
        out.push(n)
        seen.add(n.id)
      }
    }
    // Most-recent-first — server already returns newest-first, but a
    // freshly-arrived live entry could be newer than the server's head.
    out.sort((a, b) => (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0))
    return out
  }

  const fetchNotifications = useCallback(async () => {
    if (loading) return
    setLoading(true)
    try {
      const res = await request<{ notifications: NotificationItem[] }>('/api/notifications?limit=30')
      // Merge instead of overwrite — see mergeNotifications.
      setNotifications(prev => mergeNotifications(res.notifications, prev))
    } catch { /* ignore */ }
    setLoading(false)
  }, [loading])

  // Fetch unread count on mount + opportunistic list refresh in case
  // the bell missed a notification published during page load (the WS
  // subscribes a bit AFTER mount, so anything fired in that window is
  // lost to the live channel). The unread-count fetch covers the
  // badge; refetching the list here means the panel is populated by
  // the time the user clicks the bell, no second round-trip needed.
  // M2: the badge's source of truth is the server count. Optimistic local
  // updates (mark-read) give instant feedback, but we reconcile from the server
  // after each mutation settles so the badge can't drift (a commit-then-fail
  // rollback over-counting, or a double-fire double-decrementing).
  const refreshUnreadCount = useCallback(() => {
    request<{ count: number }>('/api/notifications/unread-count')
      .then(res => setUnreadCount(res.count))
      .catch(() => {})
  }, [])

  useEffect(() => {
    refreshUnreadCount()
    fetchNotifications()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Listen for live notifications
  useEffect(() => {
    if (!onSubscribe) return
    return onSubscribe((n) => {
      // Silent system signals (refresh pings, repoint toasts) — never shown
      // in the bell; the registry flags them.
      if (describeNotification(n.type, (n.data ?? {}) as NotificationData).silent) return
      setUnreadCount(c => c + 1)
      setNotifications(prev => {
        // Dedup: if the same id (e.g. fetch already landed it) is
        // present, keep the existing row instead of duplicating.
        if (prev.some(p => p.id === n.id)) return prev
        // F24: a live-pushed connection_declined that can't be reconnected must
        // not render its "Send a new request" action before the refetch lands the
        // authoritative flag — the bell gate treats a missing `actionable` as
        // actionable. Stamp it false up front; other types keep server behavior.
        const declinedNonActionable =
          n.type === 'connection_declined' &&
          !(n.data as { canReconnect?: boolean } | null | undefined)?.canReconnect
        // SAME TREATMENT FOR A ROW WITH NO TENANT. The cross-tenant fix
        // (full review 2, finding 2) routes a chat-storage action to
        // `n.tenantId`, and the LIVE path has never carried it: the WS
        // frame sends `event.notification` and drops `event.tenantId`,
        // its sibling. The list endpoint fills it; the push does not.
        //
        // Dana administers A and B, is acting in A with the panel open,
        // and Bob asks for space in B. The row appears instantly with a
        // live Decline button and no tenant, so `n.tenantId ?? tenantId`
        // sends it to A: Bob's request in B is never answered, never
        // cleared, and he cannot re-ask for two days — or, if he also has
        // an open request in A, the decline lands on the WRONG one.
        //
        // The debounced refetch below repairs the row, but it opens with
        // `if (loading) return` and schedules no retry, so a refetch
        // already in flight skips the repair entirely and the row stays
        // tenant-less until the panel closes.
        //
        // Not actionable until the authoritative row arrives. The real fix
        // is one additive field on the WS frame — `features/realtime/
        // server/websocket.ts`, server-side, written up for Season.
        const chatStorageWithoutTenant =
          n.type === 'chat_storage_more_space' && !(n as { tenantId?: string }).tenantId
        return [{
          id: n.id,
          type: n.type,
          title: n.title,
          body: n.body,
          data: n.data,
          read: false,
          ...(declinedNonActionable || chatStorageWithoutTenant ? { actionable: false as const } : {}),
          createdAt: n.createdAt,
        }, ...prev]
      })
      // Refetch defensively after a small debounce — if the user's WS
      // missed an earlier notification (subscribed after a prior
      // publish), this catches it up the moment ANY new notification
      // arrives. Cheap because the fetch endpoint is small and the
      // loading guard suppresses overlap.
      fetchNotifications()
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSubscribe])

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const handleToggle = useCallback(() => {
    // Refetch every open. The bell may have been mounted minutes ago;
    // the list cached at mount is stale by now. The loading guard
    // suppresses overlap if the user mashes the button.
    if (!open) fetchNotifications()
    setOpen(o => !o)
  }, [open, fetchNotifications])

  const handleMarkRead = useCallback(async (id: string) => {
    // N3 + M2: optimistically mark the row read and drop the badge for instant
    // feedback, then reconcile the badge from the server after the PATCH settles
    // (success OR failure). The server count is authoritative, so this self-heals
    // both a commit-then-fail (server actually read it → badge stays down) and a
    // double-fire (server count is the real number) — no fragile manual rollback.
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n))
    setUnreadCount(c => Math.max(0, c - 1))
    try {
      await request(`/api/notifications/${id}/read`, { method: 'PATCH' })
    } catch (err) {
      // #1316: dropped because its session ended — the server has the PATCH; nothing to roll back
      if (isSessionEndedError(err)) return
      // Roll the LIST row back so it isn't shown read while the server has it
      // unread; the badge is reconciled below regardless.
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: false } : n))
    } finally {
      refreshUnreadCount()
    }
  }, [refreshUnreadCount])

  const handleMarkAllRead = useCallback(async () => {
    // Snapshot the prior list so a failed read-all can restore it — otherwise
    // the optimistic all-read would stick while the reconciled badge shows a
    // nonzero count (the same list/badge disagreement M2 fixes for single read).
    // Capture inside the updater (not the closure — this useCallback's
    // `notifications` is stale) so `snapshot` is the CURRENT list. It's assigned
    // before the catch reads it because React flushes this batched update when
    // the synchronous handler unwinds, which precedes the awaited microtask.
    // KEEP this setNotifications BEFORE the first `await`, or that ordering breaks.
    let snapshot: NotificationItem[] | null = null
    setNotifications(prev => { snapshot = prev; return prev.map(n => ({ ...n, read: true })) })
    setUnreadCount(0)
    try {
      await request('/api/notifications/read-all', { method: 'PATCH' })
    } catch (err) {
      if (snapshot && !isSessionEndedError(err)) setNotifications(snapshot)   // #1316: a dropped answer rolls nothing back
    } finally {
      refreshUnreadCount() // badge reconciles to server truth either way
    }
  }, [refreshUnreadCount])

  function formatTime(iso: string): string {
    const d = new Date(iso)
    const diff = Date.now() - d.getTime()
    if (diff < 60_000) return 'just now'
    if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m ago`
    if (diff < 86400_000) return `${Math.floor(diff / 3600_000)}h ago`
    return d.toLocaleDateString()
  }

  // #11c: decorate via the registry + resolve the actor ONCE per notifications
  // change — not on every unrelated re-render (unread tick, parent state). One
  // flat, newest-first list — NO per-group ("Connections" / "Reshares" /
  // "System") sections; `notifications` is already sorted newest-first by
  // mergeNotifications, and `.map` preserves that order.
  const decorated = useMemo(() =>
    notifications
      .map(n => ({
        n,
        desc: describeNotification(n.type, (n.data ?? {}) as NotificationData),
        actor: notificationActor(n.type, (n.data ?? {}) as NotificationData),
      }))
      .filter(x => !x.desc.silent),
    [notifications])

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={handleToggle}
        className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors relative ${
          open
            ? 'themed-bg-tertiary themed-icon-active'
            : 'themed-icon hover:themed-icon-active hover:themed-bg-tertiary'
        }`}
        data-tooltip="Notifications"
        // Beside the bell only on the vertical rail. In the mobile header the
        // bell has a neighbour to its right (the burger), so a hint placed
        // there covers it; below is the host's default and is correct for
        // anything laid out horizontally.
        data-tooltip-placement={placement === 'rail' ? 'right' : undefined}
        // aria-label pins the accessible name so it stays "Notifications"
        // even when the unread badge below contains text. Without it, the
        // badge's count number becomes the button's accessible name (a
        // screen reader hears "1, button" instead of "Notifications"; e2e
        // selectors keyed on getByRole('button', { name: 'Notifications' })
        // also break).
        aria-label="Notifications"
      >
        <Bell size={20} className="pointer-events-none" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold bg-red-500 text-white rounded-full px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className={`${
          placement === 'mobile'
            ? 'fixed inset-x-2 top-14 z-[70] max-h-[72dvh]'
            : `absolute ${placement === 'top-right' ? 'right-0 top-full mt-2' : 'left-full bottom-0 ml-2'} z-50 w-80 max-w-[calc(100vw-1rem)] max-h-[480px]`
        } ws-menu ws-anim-menu-in flex flex-col overflow-hidden`}
          style={{ transformOrigin: placement === 'top-right' ? 'top right' : placement === 'mobile' ? 'top center' : 'bottom left' }}
        >
          {/* The glass menu surface (docs/DESIGN_DOC.md §10.5 — one look for every floating menu): the header is
              split off by `ws-menu-sep`, rows are rounded with the kit's hover wash, no drawn row borders. */}
          <div className="flex items-center justify-between px-2.5 py-1.5 shrink-0">
            <span className="text-sm font-medium themed-text">Notifications</span>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="p-1 rounded themed-text-muted hover:themed-text hover:themed-bg-tertiary transition-colors"
                  title="Mark all as read"
                >
                  <CheckCheck size={14} />
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="p-1 rounded themed-text-muted hover:themed-text hover:themed-bg-tertiary transition-colors"
              >
                <X size={14} />
              </button>
            </div>
          </div>

          <div className="ws-menu-sep shrink-0" />
          <div className="flex-1 overflow-y-auto space-y-0.5">
            {(() => {
              // Decorate computed once in `decorated` (useMemo above).
              if (decorated.length === 0) {
                return <div className="px-3 py-8 text-center themed-text-muted text-sm">No notifications</div>
              }
              // Row order for the entrance stagger (ws-anim-row, capped in CSS).
              let rowIndex = 0
              const renderRow = ({ n, desc, actor }: { n: NotificationItem; desc: ReturnType<typeof describeNotification>; actor: ReturnType<typeof notificationActor> }) => {
                // Actionable iff the TYPE has a registry action AND the server
                // hasn't marked THIS row stale. Dead-end types have an always-live
                // action + no server stamp, so they're clickable too.
                const isActionable = !!onAction && !!desc.action && n.actionable !== false
                // Settled: the request moved on, so the row reports how it ended
                // (and may offer a follow-on step) instead of going blank.
                const outcome = isActionable ? undefined : resolveNotificationOutcome(n.type, n.requestState, n.data as NotificationData)
                const clickable = isActionable || (!!onAction && !!outcome?.action)
                const actionLabel = (isActionable ? desc.action?.label : outcome?.action?.label) ?? 'Take action'
                return (
                  <div
                    key={n.id}
                    style={{ ['--i' as string]: rowIndex++ }}
                    onClick={clickable ? () => { if (!n.read) handleMarkRead(n.id); onAction!(n); setOpen(false) } : undefined}
                    className={`ws-anim-row px-2.5 py-2 rounded-lg hover:bg-white/10 [.theme-light_&]:hover:bg-[rgba(30,32,48,0.08)] transition-colors ${
                      clickable ? 'cursor-pointer' : 'cursor-default'
                    } ${!n.read ? 'bg-brand/5' : ''}`}
                  >
                    <div className="flex items-start gap-2.5">
                      {/* Facebook-style: the actor's avatar (who did the thing)
                          fronts the row — shared initials + name-derived color. */}
                      {(() => {
                        const label = actor?.name || actor?.email
                        return label ? (
                          <div className={`w-9 h-9 rounded-full ${avatarBg(actor!.email || actor!.name || label)} flex items-center justify-center text-white text-xs font-semibold shrink-0`} title={label}>
                            {getInitials(label)}
                          </div>
                        ) : (
                          <div className="w-9 h-9 rounded-full themed-bg-tertiary flex items-center justify-center shrink-0">
                            <Bell size={15} className="themed-text-muted" />
                          </div>
                        )
                      })()}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-brand shrink-0" />}
                          <span className="text-sm themed-text">{desc.title}</span>
                          {outcome && (
                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full shrink-0 ${BADGE_TONE[outcome.badge.tone]}`}>
                              {outcome.badge.text}
                            </span>
                          )}
                        </div>
                        {/* 3 lines, not 2: a body that names a party in full
                            ("… with Dev User <dev@demo.local> (receiving as
                            Personal).") needs 3 at this width, and clamping at 2
                            ate the trailing clause. `title` is the backstop — no
                            clamp can fit a pathological org/name/email (~5
                            lines), so the full text stays available on hover. */}
                        {desc.body && (
                          <p className="text-xs themed-text-muted mt-0.5 line-clamp-3" title={desc.body}>{desc.body}</p>
                        )}
                        <span className="text-[10px] themed-text-muted mt-0.5 block">{formatTime(n.createdAt)}</span>
                        {clickable && (
                          <span className="mt-0.5 inline-flex items-center gap-3">
                            <span className="text-[10px] text-blue-400 inline-flex items-center gap-0.5">
                              {actionLabel} <ChevronRight size={10} />
                            </span>
                            {/* A SECOND action, for rows whose two replies
                                are equally normal (#758: raise a member's
                                limit, or say no). With one, the other reply
                                becomes "ignore the row" — and an ignored
                                request is what the asker cannot tell from a
                                refused one.
                                It stops the row's own click, which is the
                                primary action: nesting a button inside a
                                clickable row means every click here would
                                otherwise fire both. */}
                            {isActionable && desc.secondaryAction && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  if (!n.read) handleMarkRead(n.id)
                                  onAction!({ ...n, secondary: true } as NotificationItem)
                                  setOpen(false)
                                }}
                                className="text-[10px] themed-text-muted hover:themed-text"
                              >
                                {desc.secondaryAction.label}
                              </button>
                            )}
                          </span>
                        )}
                      </div>
                      {!n.read && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleMarkRead(n.id) }}
                          className="p-0.5 rounded themed-text-muted hover:themed-text shrink-0"
                          title="Mark as read"
                        >
                          <MailOpen size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                )
              }
              // One flat, newest-first list — no category sections.
              return decorated.map(renderRow)
            })()}
          </div>
        </div>
      )}
    </div>
  )
}
